import { Loader2, RefreshCw, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/infra/utils';
import type { OneClickReceipt, OneClickReceiptState } from '@/types/kalshi';
import { formatPriceDollarsAsCents } from './OscarSequenceReceipt';
import {
  LIVE_HOTKEY_ACTION_IDS,
  LIVE_HOTKEY_REGISTRY,
  hotkeyLabelFor,
  type LiveHotkeyMap,
  type LiveHotkeyGrants,
} from '@/lib/trading/liveHotkeyRegistry';
import { useLiveHotkeyGrants, useLiveHotkeyMap } from '@/hooks/useLiveHotkeys';

/**
 * #314 — durable one-click receipts.
 *
 * Presentation only. Every field comes from the relay's projection over the
 * trades audit; this component derives nothing about what happened at the
 * venue and never upgrades a state the relay refused to call settled.
 *
 * Price formatting is reused from the #302 card rather than reimplemented —
 * shifting the exact decimal STRING is the only correct transform, and having
 * two copies of that rule is how one of them drifts.
 */

const STATE_LABEL: Record<OneClickReceiptState, string> = {
  rejected: 'Rejected',
  resting: 'Resting',
  // 'Partial fill', not 'Partially filled': the badge shares its row with the
  // one-line summary and is shrink-0, so every extra badge pixel is taken
  // from the scannable side/qty/price/fills/time line (#612).
  partially_filled: 'Partial fill',
  filled: 'Filled',
  cancelled: 'Cancelled',
  unknown: 'Unknown',
};

const STATE_CLASS: Record<OneClickReceiptState, string> = {
  rejected: 'bg-negative/10 text-negative',
  resting: 'bg-muted text-muted-foreground',
  partially_filled: 'bg-amber-500/10 text-amber-500',
  filled: 'bg-positive/10 text-positive',
  cancelled: 'bg-muted text-muted-foreground',
  // Amber, never neutral: unknown means "go look", not "nothing happened".
  unknown: 'bg-amber-500/10 text-amber-500',
};

function timeLabel(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  return new Date(ms).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/** Fill counts are fractional; render whatever precision is really there
 *  rather than rounding it to a tidier-looking integer. */
function fillLabel(receipt: OneClickReceipt): string | null {
  if (receipt.filledCount <= 0) return null;
  const filled = String(receipt.filledCount);
  return receipt.count == null ? `${filled} filled` : `${filled}/${receipt.count}`;
}

/**
 * Short tag for the LT2 input-method audit stamp. Absent on pre-LT2 rows;
 * unknown strings render verbatim rather than being guessed at.
 *
 * #725 — the stamp names the ACTION (`hotkey-a` = the passive top/left post),
 * so the tag is resolved through the registry rather than by hand. Two
 * consequences worth stating:
 *
 *   • W/E receipts used to render the raw string `hotkey-w`, because only a/d
 *     were mapped. Every order action now has a tag.
 *   • The tag shows the key bound RIGHT NOW. That is deliberate for a live
 *     receipt strip a trader reads mid-session — it answers "which key did
 *     that" in the vocabulary currently on their keyboard. The durable audit
 *     record keeps the stable action id regardless.
 */
function inputMethodTag(
  inputMethod: string | null | undefined,
  map: LiveHotkeyMap,
  grants: LiveHotkeyGrants,
): { tag: string; title: string } | null {
  if (inputMethod == null) return null;
  if (inputMethod === 'mouse') return null; // mouse is the default — no tag
  const action = LIVE_HOTKEY_ACTION_IDS.find(
    (id) => LIVE_HOTKEY_REGISTRY[id].inputMethod === inputMethod,
  );
  if (!action) return { tag: inputMethod, title: `Placed via ${inputMethod}` };
  const meta = LIVE_HOTKEY_REGISTRY[action];
  // ⚠️ The tooltip names the ACTION and marks the key as the CURRENT one.
  //
  // Receipts are durable, so a trader who rebinds later would otherwise read
  // "Placed via hotkey J" against an order actually placed with A — a false
  // statement on a trading surface. The stored stamp records the action, not
  // the keystroke, so naming a past key is something this row genuinely
  // cannot do; saying so is the honest form.
  return {
    tag: hotkeyLabelFor(map, action, grants),
    title: `Placed via the ${meta.label.toLowerCase()} hotkey — currently ${hotkeyLabelFor(map, action, grants)}`,
  };
}

function ReceiptRow({ receipt }: { receipt: OneClickReceipt }) {
  const price = formatPriceDollarsAsCents(receipt.priceDollars);
  const fills = fillLabel(receipt);
  const hotkeys = useLiveHotkeyMap();
  const grants = useLiveHotkeyGrants();
  const inputTag = inputMethodTag(receipt.inputMethod, hotkeys, grants);
  return (
    <div data-testid={`one-click-receipt-${receipt.clientOrderId}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span
          data-testid={`one-click-receipt-state-${receipt.clientOrderId}`}
          className={cn(
            'shrink-0 rounded px-1 py-0.5 text-[11px] font-semibold',
            STATE_CLASS[receipt.state],
          )}
        >
          {STATE_LABEL[receipt.state]}
          {inputTag && (
            <span
              className="text-muted-foreground ml-1 font-mono font-normal"
              title={inputTag.title}
            >
              ⌨{inputTag.tag}
            </span>
          )}
        </span>
        {/* One line, full text, no hover fallback: this is the row a trader's
            eye scans down, so it must not wrap — and it must FIT, because an
            ellipsis would hide reconciliation data (the timestamp keeps its
            seconds deliberately). If it stops fitting, the density capture's
            geometric fit check fails rather than a truncation hiding it. */}
        <span className="text-foreground whitespace-nowrap text-right font-mono text-[11px] leading-tight">
          {receipt.side ? receipt.side.toUpperCase() : '—'}
          {receipt.count != null ? ` · ${receipt.count}ct` : ''}
          {` @ ${price ?? '—'}`}
          {fills ? ` · ${fills}` : ''}
          {' · '}
          <span className="text-muted-foreground">{timeLabel(receipt.createdAt)}</span>
          {/* A cancellation is reported alongside the fill state, never instead
              of it: a partly-filled order that was then cancelled has two facts
              and collapsing them into one label misstates the position. */}
          {receipt.cancelled && receipt.state !== 'cancelled' && (
            <XCircle
              data-testid={`one-click-receipt-cancelled-${receipt.clientOrderId}`}
              size={11}
              className="text-muted-foreground ml-1 inline align-[-1px]"
            />
          )}
        </span>
      </div>
      {receipt.message && (
        <div
          data-testid={`one-click-receipt-message-${receipt.clientOrderId}`}
          className="text-muted-foreground min-w-0 break-words text-right text-[10px] leading-tight"
          title={receipt.message}
        >
          {receipt.message}
        </div>
      )}
    </div>
  );
}

export interface OneClickReceiptsProps {
  receipts: OneClickReceipt[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

export function OneClickReceipts({ receipts, loading, error, onRefresh }: OneClickReceiptsProps) {
  return (
    <div
      data-testid="one-click-receipts"
      className="border-border space-y-1.5 rounded-lg border px-3 py-2 text-xs"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground font-semibold uppercase tracking-wide">
          One-click receipts
        </span>
        <div className="flex items-center gap-2">
          {loading && (
            <Loader2 data-testid="one-click-receipts-loading" size={11} className="animate-spin" />
          )}
          <Link to="/trades" className="text-muted-foreground hover:text-foreground transition-colors">
            All trades
          </Link>
          <button
            type="button"
            onClick={onRefresh}
            className="text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
          >
            <RefreshCw size={11} />
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="text-negative">{error}</div>}

      {!error && receipts.length === 0 && (
        <div data-testid="one-click-receipts-empty" className="text-muted-foreground">
          No one-click orders recorded for this market.
        </div>
      )}

      {!error && receipts.length > 0 && (
        <div className="space-y-0.5">
          {receipts.map((receipt) => (
            <ReceiptRow key={receipt.clientOrderId} receipt={receipt} />
          ))}
        </div>
      )}
    </div>
  );
}
